import {create} from 'zustand';
import {DocumentTemplate} from 'shared-types';
import {CreateDocumentTemplateRequest} from 'shared-types/template';
import {documentApi} from '../../services/api';


interface DocumentTemplateState {
    documentTemplates: DocumentTemplate[];
    currentDocumentTemplate: DocumentTemplate | null;
    loading: boolean;
    error: string | null;

    getDocumentTemplates: () => Promise<void>;
    getDocumentTemplateById: (id: string | undefined) => Promise<DocumentTemplate | null>;
    createDocumentTemplate: (templateData: CreateDocumentTemplateRequest) => Promise<DocumentTemplate | null>;
    deleteDocumentTemplate: (id: string) => Promise<DocumentTemplate | null>;
    updateDocumentTemplate: (id: string, templateData: CreateDocumentTemplateRequest) => Promise<DocumentTemplate | null>;
    previewDocumentTemplate: (id: string, variables: Record<string, string>) => Promise<string | null>;
    clearError: () => void;
    setCurrentDocumentTemplate: (documentTemplate: DocumentTemplate | null) => void;
}

export const useDocumentTemplateStore = create<DocumentTemplateState>((set) => ({
    documentTemplates: [],
    currentDocumentTemplate: null,
    loading: false,
    error: null,

    getDocumentTemplates: async () => {
        set({loading: true, error: null});
        try {
            const templates = await documentApi.getTemplates();
            set({documentTemplates: templates, loading: false});
        } catch (error) {
            set({error: 'Error getting all documentTemplates', loading: false});
            throw error;
        }
    },

    getDocumentTemplateById: async (id: string | undefined): Promise<DocumentTemplate | null> => {
        if (!id) {
            set({error: 'Document template ID is required'});
            return null;
        }
        set({loading: true, error: null});
        try {
            const data = await documentApi.getTemplateById(id);
            set({currentDocumentTemplate: data, loading: false});
            console.log("success");
            return data;
        } catch (error) {
            set({error: 'Error getting documentTemplate by id', loading: false});
            throw error;
        }
    },

    createDocumentTemplate: async (templateData: CreateDocumentTemplateRequest): Promise<DocumentTemplate | null> => {
        set({loading: true, error: null});
        try {
            const now = new Date().toISOString();

            const templateToInsert = {
                name: templateData.name,
                type: templateData.type,
                language: templateData.language,
                template: templateData.template,
                variables: templateData.variables,
                isDefault: templateData.isDefault,
                active: templateData.active,
                createdAt: now,
                updatedAt: now
            };
            const newDocumentTemplate = await documentApi.createTemplate({newDocuments: templateToInsert});
            set(state => ({
                documentTemplates: [...state.documentTemplates, newDocumentTemplate],
                loading: false
            }));
            return newDocumentTemplate;
        } catch (error) {
            set({error: 'Error creating documentTemplate', loading: false});
            throw error;
        }
    },

    deleteDocumentTemplate: async (id: string): Promise<DocumentTemplate | null> => {
        set({loading: true, error: null});
        try {
            const deletedDocumentTemplate = await documentApi.deleteTemplate(id);
            set(state => ({
                documentTemplates: state.documentTemplates.filter(documentTemplate => documentTemplate.id !== id),
                currentDocumentTemplate: state.currentDocumentTemplate?.id === id ? null : state.currentDocumentTemplate,
                loading: false
            }));
            return deletedDocumentTemplate;
        } catch (error) {
            set({error: 'Error removing document template', loading: false});
            throw error;
        }
    },

    updateDocumentTemplate: async (id: string, templateData: CreateDocumentTemplateRequest): Promise<DocumentTemplate | null> => {
        set({loading: true, error: null});
        try {
            const now = new Date().toISOString();
            const templateToUpdate = {
                name: templateData.name,
                type: templateData.type,
                language: templateData.language,
                template: templateData.template,
                variables: templateData.variables,
                isDefault: templateData.isDefault,
                createdAt: now, // אם רוצים לשמור את התאריך המקורי
                updatedAt: now
            };
            console.log(templateToUpdate, "Updating template with data before 2 send");

            const updatedDocumentTemplate = await documentApi.updateTemplate(id, templateToUpdate);
            set(state => ({
                documentTemplates: state.documentTemplates.map(documentTemplate =>
                    documentTemplate.id === id ? updatedDocumentTemplate : documentTemplate
                ),
                currentDocumentTemplate: state.currentDocumentTemplate?.id === id ? updatedDocumentTemplate : state.currentDocumentTemplate,
                loading: false
            }));
            return updatedDocumentTemplate;
        } catch (error) {
            set({error: 'Error updating document template', loading: false});
            throw error;
        }
    },

    previewDocumentTemplate: async (id: string, variables: Record<string, string>): Promise<string | null> => {
        set({loading: true, error: null});
        try {
            const response = await documentApi.previewTemplate(id, variables);
            const renderedHtml = response?.renderedHtml;
            set({loading: false});
            return renderedHtml ?? null;
        } catch (error) {
            set({error: 'Error previewing document template', loading: false});
            throw error;
        }
    },

    setCurrentDocumentTemplate: (documentTemplate: DocumentTemplate | null) => {
        set({currentDocumentTemplate: documentTemplate});
    },

    clearError: () => {
        set({error: null});
    }
}));
